import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import type { QuizSelect } from "@-/dev.improve.haniverse.assets.slides";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useMorphemeDetailsTileSliceQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { GraphemeMorphemeGlyphTileUi } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { useContext } from "react";

import { QuizCardContext } from "#quiz-card-context";

type QuizOptionResult = "correct" | "missed" | "neutral" | "wrong";

/** How an option reads once the answer is checked. */
function quizOptionResult({
  correct,
  isAnswered,
  isSelected,
}: {
  correct: boolean;
  isAnswered: boolean;
  isSelected: boolean;
}): QuizOptionResult {
  if (!isAnswered) {
    return "neutral";
  }
  if (correct) {
    return isSelected ? "correct" : "missed";
  }
  return isSelected ? "wrong" : "neutral";
}

/** The row's border: the selection before Check, then the answer key. Always 2pt, so no shift. */
const RESULT_CLASS_NAME: Record<QuizOptionResult, string | null> = {
  correct: "border-emerald-500",
  // A correct option the learner left out: marked, but not as if they had chosen it.
  missed: "border-dashed border-emerald-500",
  neutral: null,
  wrong: "border-destructive",
};

const RESULT_ACCESSIBILITY_SUFFIX: Record<QuizOptionResult, string> = {
  correct: ", correct",
  missed: ", a correct answer you missed",
  neutral: "",
  wrong: ", not correct",
};

/**
 * The name Haniverse gives the glyph (丨 "stick", 一 "line"): its component mnemonic, as its lesson
 * taught it, else its meaning. `isMnemonic` sets it in italics, like mnemonics everywhere else.
 */
function useQuizOptionName(glyph: string): { isMnemonic: boolean; name: string } | null {
  const { data } = useQuery(useMorphemeDetailsTileSliceQueryOptions({ morpheme: glyph }));
  if (data?.morphemeType !== "grapheme") {
    return null;
  }
  const mnemonic = data.mnemonicTerms?.find((term) => term.trim() !== "");
  if (mnemonic !== undefined) {
    return { isMnemonic: true, name: mnemonic };
  }
  return data.back === undefined || data.back.trim() === ""
    ? null
    : { isMnemonic: false, name: data.back };
}

/** A radio dot for `select="one"`, a checkbox for `select="all"`; drawn only, the row is the control. */
function QuizOptionIndicator({ isSelected, select }: { isSelected: boolean; select: QuizSelect }) {
  return (
    <View
      className={cn(
        "size-5 items-center justify-center border border-input bg-background",
        select === "one" ? "rounded-full" : "rounded-xs",
        isSelected && "border-primary",
        isSelected && select === "all" && "bg-primary",
      )}
    >
      {isSelected ? (
        select === "one" ? (
          <View className="size-2.5 rounded-full bg-primary" />
        ) : (
          <Icon className="text-primary-foreground" name="LuCheckBold" size={14} />
        )
      ) : null}
    </View>
  );
}

function QuizOptionRow({ correct, glyph }: { correct: boolean; glyph: string }) {
  const context = useContext(QuizCardContext);
  if (context === null) {
    throw new Error("<Quiz> renders only inside a quiz card");
  }
  const { answer, isInteractive, quiz, selected, toggle } = context;
  const optionName = useQuizOptionName(glyph);
  const isSelected = selected.has(glyph);
  const result = quizOptionResult({ correct, isAnswered: answer !== null, isSelected });
  // Keyed by quiz: the successor card can be another quiz with the same glyphs, and web keeps it
  // in the tree. `nativeID` gives web the same id, since its aria-label (the glyph) would win.
  const optionID = `quiz-${quiz.key}-option-${glyph}`;
  const label = optionName === null ? glyph : `${glyph}, ${optionName.name}`;
  return (
    <Pressable
      accessibilityLabel={`${label}${RESULT_ACCESSIBILITY_SUFFIX[result]}`}
      accessibilityRole={quiz.select === "one" ? "radio" : "checkbox"}
      accessibilityState={{
        checked: isSelected,
        disabled: !isInteractive || answer !== null,
      }}
      className={cn(
        "w-full flex-row items-center gap-4 rounded-xs border-2 border-border bg-surface p-2 pr-4",
        answer === null && isSelected && "border-primary/60 bg-primary/5",
        RESULT_CLASS_NAME[result],
        result === "neutral" && answer !== null && "opacity-50",
      )}
      disabled={!isInteractive || answer !== null}
      nativeID={optionID}
      onPress={() => {
        toggle(glyph);
      }}
      testID={optionID}
    >
      {/* The tile only draws: the option is this pressable, which owns its role and state. */}
      <View className="size-14" pointerEvents="none">
        <GraphemeMorphemeGlyphTileUi
          grapheme={glyph}
          // Stage colors and tone colors would hint at answers; every option looks alike.
          stage="unadded"
          uncolored
        />
      </View>
      <Text
        className={cn("min-w-0 flex-1 text-lg", optionName?.isMnemonic === true && "italic")}
        numberOfLines={2}
      >
        {optionName?.name ?? ""}
      </Text>
      <QuizOptionIndicator isSelected={isSelected} select={quiz.select} />
    </Pressable>
  );
}

/**
 * The `<Quiz>` MDX component: the quiz's options as a vertical stack, in this showing's order.
 * Drawn from the quiz manifest (the answer key the build read from the same `<Quiz>`), never from
 * its MDX children.
 */
export function QuizOptions() {
  const context = useContext(QuizCardContext);
  if (context === null) {
    throw new Error("<Quiz> renders only inside a quiz card");
  }
  return (
    <View
      accessibilityRole={context.quiz.select === "one" ? "radiogroup" : undefined}
      className="w-full max-w-md gap-2 self-center"
      testID="quiz-options"
    >
      {context.options.map((option) => (
        <QuizOptionRow correct={option.correct} glyph={option.glyph} key={option.glyph} />
      ))}
    </View>
  );
}

/** `<QuizOption>` declares an option for the build; `<Quiz>` draws every option itself. */
export function QuizOptionDeclaration() {
  return null;
}

/** What the learner is asked to do, then how they did. */
export function QuizStatusLine() {
  const context = useContext(QuizCardContext);
  if (context === null) {
    throw new Error("The quiz status line renders only inside a quiz card");
  }
  const { answer, quiz } = context;
  if (answer === null) {
    return (
      <Text className="text-center text-sm" colorVariant="muted" testID="quiz-instructions">
        {quiz.select === "one" ? "Choose one, then check." : "Select every answer, then check."}
      </Text>
    );
  }
  return (
    <Text
      accessibilityLiveRegion="polite"
      className={cn(
        "text-center text-base font-semibold",
        answer.correct ? "text-emerald-600" : "text-destructive",
      )}
      testID="quiz-result"
    >
      {answer.correct
        ? "Correct!"
        : quiz.select === "one"
          ? "Not quite. The correct answer is marked in green."
          : "Not quite. The correct answers are marked in green."}
    </Text>
  );
}
